import * as React from "react";
import { inject, MobXProviderContext, observer } from "mobx-react";
import { UitkNewDatePicker } from "uitk-react-new-date-picker";
import { Link as RouterLink } from "react-router-dom";
import { DialogRoute, RouteStore } from "bernie-core";
import { Layer } from "bernie-view";
import { DatesProps } from "./typings";
import { DateFieldConfig } from "src/stores/wizard/config";
import { FlightWizardState } from "src/stores/wizard/state/flight";
import { PackageWizardState } from "src/stores/wizard/state/package";
import { DateState } from "src/stores/wizard/state/global";
import { Context } from "bernie-context";
import { UitkLayoutGridItem } from "uitk-react-layout-grid";
import { useLocalization } from "@shared-ui/localization-context";
import { CarWizardState, CruiseWizardState, HotelWizardState } from "src/stores/wizard/state";
import { ACTION_DONE, ACTION_OPEN, getTracker } from "utils/TrackingUtils";
import { carSubLOBs } from "../../../stores/wizard/state/typings";

export const Dates: React.FunctionComponent<DatesProps> = inject("analytics")(
  observer(
    ({
      singleDate,
      singleDateSelect,
      colSpan,
      lobState,
      isPartialStay,
      multiLegIndex,
      additionalSingleDate,
      stackedDates,
      startId,
      endId,
      analytics,
      setInputsRefs,
      autoOpenCalendar,
      toggleDatePicker,
      useHiddenInputs = true,
    }) => {
      const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
      const context: Context = React.useContext(MobXProviderContext).context;
      const [showDatePicker, setShowDatePicker] = React.useState<boolean>(false);
      const initialDaysBookableInAdvance = React.useRef<number | null>(null);
      const { formatText, formatDateString, formatDate } = useLocalization();
      const [dateConfig, updateDates, dateState, dateStartInvalidKey, dateEndInvalidKey] = ((): [
        DateFieldConfig,
        (start: Date, end: Date, multiLegIndex?: number, additionalSingleDate?: boolean) => void,
        DateState,
        string,
        string
      ] => {
        if (!multiLegIndex && isPartialStay && lobState instanceof PackageWizardState) {
          return [
            lobState.config.partialStayDate,
            lobState.updatePartialStayDateSelection,
            lobState.partialStayDate,
            lobState.partialStayStartDateInvalidKey,
            lobState.partialStayEndDateInvalidKey,
          ];
        } else if (lobState instanceof PackageWizardState && lobState.subLOBState === "hc") {
          return [
            lobState.config.subLOB.hotelCar.date,
            lobState.updateDateSelection,
            lobState.date,
            lobState.dateStartInvalidKey,
            lobState.dateEndInvalidKey,
          ];
        } else if (isPartialStay && lobState instanceof FlightWizardState) {
          return [
            lobState.config.hotelDate,
            lobState.updateHotelDateSelection,
            lobState.hotelDate,
            lobState.hotelStartDateInvalidKey,
            lobState.hotelEndDateInvalidKey,
          ];
        } else if (
          multiLegIndex !== undefined &&
          (lobState instanceof FlightWizardState || lobState instanceof PackageWizardState) &&
          lobState.config.multiCityFlight
        ) {
          const dateStartInvalidKeyString =
            lobState.legsDateStartInvalidKey.length > multiLegIndex
              ? lobState.legsDateStartInvalidKey[multiLegIndex] || ""
              : "";

          return [
            lobState.config.multiCityFlight.legs[multiLegIndex].date,
            lobState.updateLegDate,
            lobState.listOfAdditionalLegs[multiLegIndex].date,
            dateStartInvalidKeyString,
            "",
          ];
        } else if (
          lobState instanceof CarWizardState &&
          lobState.subLOBState === carSubLOBs.groundTransportation &&
          additionalSingleDate
        ) {
          return [
            lobState.config.gtConfig.additionalSingleDate,
            lobState.updateAdditionalSingleDate,
            lobState.additionalSingleDate,
            "",
            "",
          ];
        } else if (lobState instanceof CarWizardState && lobState.subLOBState === carSubLOBs.groundTransportation) {
          return [
            lobState.config.gtConfig.date,
            lobState.updateDateSelection,
            lobState.date,
            lobState.dateStartInvalidKey,
            lobState.dateEndInvalidKey || "",
          ];
        } else {
          return [
            lobState.config.date,
            lobState.updateDateSelection,
            lobState.date,
            lobState.dateStartInvalidKey,
            lobState.dateEndInvalidKey || "",
          ];
        }
      })();

      const onOpenCB = (name?: string, isMobile?: boolean) => {
        if (toggleDatePicker) {
          toggleDatePicker();
        } else {
          setShowDatePicker(true);
        }

        const type = name === "start" ? _checkIn : _checkOut;

        track(`date.${ACTION_OPEN}.${type}`);
      };

      const onSubmitCB = (start: Date, end: Date, isMobile: boolean) => {
        if (isMobile) {
          routeStore.updateSearch("datePickerDialog");
        }

        if (toggleDatePicker) {
          toggleDatePicker();
        } else {
          setShowDatePicker(false);
        }

        if (end === undefined) {
          end = dateState.end;
        }

        if (start > end) {
          end.setDate(start.getDate() + 1);
        }

        updateDates(start, end, multiLegIndex);

        track(`dates.${ACTION_DONE}`);
      };

      const onDismissCB = (isMobile: boolean) => {
        if (isMobile) {
          routeStore.closeDialog();
        }

        if (toggleDatePicker) {
          toggleDatePicker();
        } else {
          setShowDatePicker(false);
        }
      };

      const pwaDialog = `datepicker-${dateConfig.dialogId}`;

      const routerLinkWrapper = (children: React.ReactNode) => {
        return (
          <RouterLink
            to={{
              pathname: routeStore.pathname,
              search: routeStore.mergeQueryString(routeStore.search, { pwaDialog }),
            }}
          >
            {children}
          </RouterLink>
        );
      };

      const dialogWrapper = (children: React.ReactNode, dateType: string) => {
        return (
          <DialogRoute id={pwaDialog}>
            <Layer id={`${pwaDialog}-${dateType}`} onDismiss={onDismissCB}>
              {children}
            </Layer>
          </DialogRoute>
        );
      };

      const getStartId = (defaultValue: string) => {
        return startId ? startId : defaultValue;
      };

      const getEndId = (defaultValue: string) => {
        return endId ? endId : defaultValue;
      };

      const onChangeStartDateCB = () => {
        track(_selectDates + _checkIn);
      };

      const onChangeEndDateCB = () => {
        track(_selectDates + _checkOut);
      };

      const tracker = getTracker(lobState.trackingEnabled, analytics, lobState.moduleName);

      const _checkIn = "check-in";
      const _checkOut = "check-out";
      const _selectDates = "dates.select.";
      const _partialStay = "hotel-partial-stay.";

      const track = (action: string) => {
        const prefix = isPartialStay ? _partialStay : "";
        tracker.trackEvent(prefix + action);
      };

      const dateFieldProps = {
        dialogWrapper,
        endId: getEndId("d2"), // TODO: uitk-new-date-picker not supports setting custom date value format.
        endInvalid: formatText(dateEndInvalidKey, dateConfig.maxDaysRange),
        endLabel: formatText(dateConfig.end.labelToken),
        endName: getEndId("d2"), // TODO: uitk-new-date-picker not supports setting custom date value format.
        endPlaceholder: formatText(dateConfig.end.labelToken),
        routerLinkWrapper,
        showDatePicker: typeof autoOpenCalendar === "boolean" ? autoOpenCalendar : showDatePicker,
        stacked: stackedDates,
        startId: getStartId("d1"), // TODO: uitk-new-date-picker not supports setting custom date value format.
        startInvalid: formatText(dateStartInvalidKey, dateConfig.maxDaysRange),
        startLabel: formatText(dateConfig.start.labelToken),
        startName: getStartId("d1"), // TODO: uitk-new-date-picker not supports setting custom date value format.
        startPlaceholder: formatText(dateConfig.start.labelToken),
        onChange: () => null, // error thrown if no onChange handler is passed
      };

      // Certain LOBs dont support ISO format for their SRPs they only support local format
      // If that is the case the local raw format will be taken from bernie context
      const rawFormatForSubmission = dateConfig.ISOSubmissionSupport ? dateConfig.format : context.localDateFormat;

      /**
       * Builds and returns a date starting
       * from tomorrow in the given format in the
       * rawFormatForSubmission as a string value.
       */
      const getStartValidDate = (raw: string): string => {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        const tomorrowDate = formatDate(tomorrow, { raw });

        return tomorrowDate;
      };

      if (lobState instanceof HotelWizardState && (lobState.isAddACarChecked || lobState.isAddAFlightChecked)) {
        lobState.overrideConfig(() => {
          if (
            dateConfig.softPackage &&
            dateConfig.daysBookableInAdvance !== dateConfig.softPackage.daysBookableInAdvance
          ) {
            initialDaysBookableInAdvance.current = dateConfig.daysBookableInAdvance;
            dateConfig.daysBookableInAdvance = dateConfig.softPackage.daysBookableInAdvance;
          }
        });
      } else if (
        initialDaysBookableInAdvance.current !== null &&
        dateConfig.daysBookableInAdvance !== initialDaysBookableInAdvance.current
      ) {
        lobState.overrideConfig(() => {
          if (initialDaysBookableInAdvance.current !== null) {
            dateConfig.daysBookableInAdvance = initialDaysBookableInAdvance.current;
          }
        });
      }

      const validStartDate =
        lobState instanceof PackageWizardState && lobState.subLOBState === "hc"
          ? getStartValidDate(rawFormatForSubmission)
          : "";

      // Show nights summary on Done button of calendar for stays, cruises and packages for stays combination only.
      const showNightsSummary =
        lobState instanceof HotelWizardState ||
        lobState instanceof CruiseWizardState ||
        (lobState instanceof PackageWizardState && lobState.subLOBState !== "fc");
      //The first div is the one that adds the UitkLayoutGridItem classes.
      //The second div with the class Dates was added so that custom classes work as expected without using !important in them.

      return (
        <UitkLayoutGridItem colSpan={colSpan}>
          <div>
            <div className="Dates">
              {/*
              TODO: uitk-new-date-picker not supports setting custom date value format.
              Add start/end hidden inputs with custom value format.
            */}
              {useHiddenInputs && (
                <>
                  <input
                    type="hidden"
                    name={dateConfig.queryParam.start}
                    value={formatDateString(dateState.start, { raw: rawFormatForSubmission })}
                  />
                  {!singleDate && (
                    <input
                      type="hidden"
                      name={dateConfig.queryParam.end}
                      value={formatDateString(dateState.end, { raw: rawFormatForSubmission })}
                    />
                  )}
                </>
              )}
              <UitkNewDatePicker
                buttonSubTextShowNights={showNightsSummary}
                dateFieldProps={dateFieldProps}
                daysBookableInAdvance={dateConfig.daysBookableInAdvance}
                doubleMonths={true}
                endDate={singleDate ? "" : formatDateString(dateState.end, { raw: dateConfig.format })}
                firstDayOfWeek={dateConfig.firstDayOfWeek}
                midnightBookingOffset={dateConfig.midnightBookingOffset}
                onDismissCB={onDismissCB}
                onOpenCB={onOpenCB}
                onSubmitCB={onSubmitCB}
                onChangeStartDateCB={onChangeStartDateCB}
                onChangeEndDateCB={onChangeEndDateCB}
                startDate={formatDateString(dateState.start, { raw: dateConfig.format })}
                singleMode={singleDate}
                onMountCB={setInputsRefs}
                singleDateSelect={singleDateSelect}
                validStartDate={validStartDate}
              />
            </div>
          </div>
        </UitkLayoutGridItem>
      );
    }
  )
);
